/**
 * Colour for a Prozo tracking status.
 *
 * Shared by the exception-label and manual-order lists so one status never
 * reads two ways. The families follow the palette the Orders tab already uses
 * for order status, so the terminal stays one system:
 *
 *   neutral  grey   — nothing has happened yet (no scan, draft)
 *   info     blue   — moving normally, in the carrier's hands
 *   good     green  — arrived (delivered, and RTO delivered: the parcel is back)
 *   warn     amber  — needs attention but not yet lost (held, delayed, failed attempt)
 *   bad      red    — went wrong (RTO, lost, damaged, cancelled)
 *
 * RTO is deliberately split: *in progress* is bad news (the parcel is coming
 * back), but `rto_delivered` is green because the journey resolved — the goods
 * are safely returned rather than missing.
 */

const TONES = {
  // Nothing has moved yet.
  order_placed: 'isNeutral',
  awb_registered: 'isNeutral',
  awb_registered_on_proship: 'isNeutral',
  manifested: 'isNeutral',
  pickup_pending: 'isNeutral',
  cancelled_pending: 'isNeutral',

  // In the carrier's hands, going the right way.
  out_for_pickup: 'isInfo',
  picked_up: 'isInfo',
  in_transit: 'isInfo',
  out_for_delivery: 'isInfo',

  // Arrived — forward or returned.
  delivered: 'isGood',
  rto_delivered: 'isGood',

  // Needs a human, but not yet a loss.
  pickup_failed: 'isWarn',
  failed_delivery: 'isWarn',
  shipment_delayed: 'isWarn',
  shipment_held: 'isWarn',
  contact_customer_care: 'isWarn',
  not_serviceable: 'isWarn',
  rto_shipment_delay: 'isWarn',
  rto_contact_customer_care: 'isWarn',

  // Went wrong.
  rto_requested: 'isBad',
  rto: 'isBad',
  rto_in_transit: 'isBad',
  rto_out_for_delivery: 'isBad',
  rto_failed: 'isBad',
  lost: 'isBad',
  damaged: 'isBad',
  cancelled_order: 'isBad',
};

/**
 * Tone for one row.
 *
 * A cancelled label outranks whatever Prozo last said: the AWB is dead, so
 * showing "In Transit" in blue would be a lie.
 */
export function progressTone(row) {
  if (!row) return 'isNeutral';
  if (row.status === 'cancelled') return 'isDead';
  const progress = row.progress || '';
  if (!progress) return 'isNeutral';
  // An unmapped status is shown, but never coloured as if it were understood.
  return TONES[progress] || 'isNeutral';
}

export default progressTone;
